import { Link } from "@remix-run/react";
import { useTranslation } from "react-i18next";

import { useAvailableCourses } from "~/api/queries/useAvailableCourses";
import { Button } from "~/components/ui/button";
import { useLanguageStore } from "~/modules/Dashboard/Settings/Language/LanguageStore";

import type { SupportedLanguages } from "@repo/shared";

interface CategoryNavigationButtonsProps {
  categoryTitle: string;
  categorySlug: string;
}

/**
 * Los dos botones del pie de una página de categoría: "todos los cursos" y "los de esta categoría".
 *
 * ═══ Por qué el segundo botón puede no existir ═══
 *
 * Este botón era la entrada VIVA a un fold roto en `/courses`. El título de la categoría sale del
 * registro de la página de categoría (`CategoryLanding.page.tsx`), NO de los cursos publicados: una
 * categoría con página y sin un solo curso publicado generaba igual un botón público "Ver cursos de
 * X" que aterrizaba en un catálogo filtrado a cero, con el aviso de "ninguno coincide" empujando la
 * primera tarjeta a 469/496/537px (390×844 / 360×740 / 1440×900) contra un techo de 300, sin que el
 * usuario tocara nada.
 *
 * Se cierran las dos puntas: `/courses` ahora normaliza el `?category=` contra las facetas reales
 * (`resolveCategory` en `Landing/lib/courseFacets.ts`), así que ninguna query string puede vaciar la
 * grilla; y acá el botón NO SE RENDERIZA si la categoría no tiene cursos publicados, porque un botón
 * que promete "los cursos de X" y lleva al catálogo completo miente.
 *
 * El conteo NO cuesta un request: `CategoryLanding.page.tsx` ya pide
 * `useAvailableCourses({ language, category: slug })` para la vidriera, y esta llamada usa los mismos
 * parámetros, o sea la misma queryKey de TanStack (el hash ordena las claves del objeto, así que el
 * orden no importa). Es la caché, no la red.
 *
 * ═══ Por qué el `?category=` lleva el TÍTULO y no el slug ═══
 *
 * Porque es lo único que el destino puede resolver, y está medido, no supuesto:
 * - `categories.slug` es INGLÉS en la base (`ai-practical`, `ai-from-zero` — SQL del 2026-07-26),
 * - el listado público NO devuelve el slug de la categoría: `course.service.ts:207` proyecta
 *   `category: getFieldByLanguage(categories.title, language)`, o sea el título localizado
 *   ("IA Práctica"),
 * - así que las facetas de `/courses` (que se derivan de ese listado) sólo conocen títulos, y
 *   `slugify("IA Práctica")` = "ia-practica" nunca va a matchear "ai-practical".
 *
 * O sea: linkear por slug haría que el filtro se descarte en silencio y el botón mostrara el catálogo
 * entero — el mismo engaño que el caso de la categoría vacía, pero más difícil de ver. Para poder
 * linkear por slug hay que exponer `categorySlug` por curso en el payload de `available-courses`; es
 * un cambio de API y va aparte. Mientras no exista, el título es el valor correcto: sale de la MISMA
 * columna y el MISMO idioma que el `category` de cada curso, así que el match es exacto.
 */
export function CategoryNavigationButtons({
  categoryTitle,
  categorySlug,
}: CategoryNavigationButtonsProps) {
  const { t } = useTranslation();
  const { language } = useLanguageStore();

  const { data: categoryCourses } = useAvailableCourses({
    language: language as SupportedLanguages,
    ...(categorySlug && { category: categorySlug }),
  });

  // Sin slug no hay con qué contar y sin cursos publicados no hay adónde ir: en los dos casos queda
  // sólo el botón de "todos los cursos", que nunca lleva a un callejón.
  const hasPublishedCourses = !!categorySlug && (categoryCourses?.length ?? 0) > 0;

  return (
    <section className="py-12">
      <div className="mx-auto flex max-w-7xl items-center justify-center gap-4 px-4 sm:px-6 lg:px-8">
        <Button variant="outline" size="lg" asChild>
          <Link to="/courses">{t("landing.categoryPage.viewAllCourses")}</Link>
        </Button>
        {hasPublishedCourses && (
          <Button size="lg" asChild>
            <Link to={`/courses?category=${encodeURIComponent(categoryTitle)}`}>
              {t("landing.categoryPage.viewCategoryCourses", { category: categoryTitle })}
            </Link>
          </Button>
        )}
      </div>
    </section>
  );
}
